import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpParams } from '@angular/common/http';
import { DiscountCategory } from '../components/price-summary/price-summary.model';
import { EnvironmentDiff } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';
import { Product } from './products.service';

export interface CategoriesCache {
    categories: Array<DiscountCategory>;
}

@Injectable({
    providedIn: 'root',
})
export class ProductsCategoryService {
    constructor(private httpService: HttpService) {}
    private categoriesCache: CategoriesCache = {
        categories: [],
    };

    private categoriesFrozen = false;

    public haveDiscountCategoriesChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public updateProductCategoryDiscountsInCache(...envDiffs: EnvironmentDiff[]) {
        const categoryDiffs = envDiffs.filter((diff) => !!diff?.added['CATEGORY-BDIS']);
        if (categoryDiffs.length) {
            const updated = categoryDiffs
                .map((envDiff) =>
                    Object.entries(envDiff.added['CATEGORY-BDIS']).map(([category, discount]) => ({
                        category,
                        discount,
                    }))
                )
                .reduce((prev, curr) => [...prev, ...curr]);
            this.updateCategories(updated);
        }
    }

    public setDiscountsFrozen(frozen: boolean, categoryToRemove: string) {
        if (frozen) {
            const categories = this.categoriesCache.categories
                .filter((c) => c.category !== categoryToRemove)
                .map((c) => ({ ...c, discount: 1 }));
            this.updateCategories(categories);
        }
        this.categoriesFrozen = frozen;
    }

    private updateCategories(updated: DiscountCategory[]) {
        if (!this.categoriesFrozen) {
            const before = JSON.stringify(this.categoriesCache.categories);
            this.categoriesCache.categories = updated;
            const after = JSON.stringify(this.categoriesCache.categories);

            if (before !== after) {
                this.haveDiscountCategoriesChanged.next(true);
            }
        }
    }

    public getProductDiscountCategoriesFromCache(products: Product[]): DiscountCategory[] {
        const discounts = this.categoriesCache.categories;
        const getDiscount = (category: string) => discounts.find((d) => d.category === category)?.discount;
        const all = getDiscount('ALL');
        const rest = getDiscount('REST');
        return products
            .map((product) => ({
                category: product.category,
                discount: all || getDiscount(product.category) || rest,
            }))
            .filter((d) => d.category && d.discount !== undefined);
    }

    public clearCategoriesCache() {
        this.categoriesCache.categories = [];
        this.categoriesFrozen = false;
    }

    public fetchProductCategoryGroupNameMap(salesOrg: string) {
        const params: HttpParams = environment.app.productCategoryGroupNameMapSalesOrg.includes(salesOrg)
            ? new HttpParams({
                  fromObject: {
                      usecase: 'qa-ch',
                  },
              })
            : undefined;

        const url = `${environment.http.configuration}material/groupMap`;
        return this.httpService.get(url, params);
    }
}
